import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Upload, FileText, Sparkles, Send, Tag, AtSign, ShieldAlert } from "lucide-react";
import api from "@/lib/api";
import { GlassCard, SectionLabel, GradientButton, GhostButton } from "@/components/primitives";
import Markdown from "@/components/Markdown";

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [active, setActive] = useState(null);
  const [summary, setSummary] = useState("");
  const [busy, setBusy] = useState(false);
  const [chat, setChat] = useState([]);
  const [q, setQ] = useState("");
  const [insights, setInsights] = useState(null);
  const [insightsBusy, setInsightsBusy] = useState(false);
  const fileRef = useRef(null);
  const [drag, setDrag] = useState(false);

  const load = async () => { const { data } = await api.get("/documents"); setDocs(data); };
  useEffect(() => { load(); }, []);

  const onUpload = async (file) => {
    const form = new FormData(); form.append("file", file);
    const { data } = await api.post("/documents/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
    setDocs((d) => [data, ...d]); setActive(data); setSummary(""); setChat([]); setInsights(data.insights || null);
  };

  const summarize = async () => {
    setBusy(true); setSummary("");
    try { const { data } = await api.post(`/documents/${active.id}/summarize`); setSummary(data.summary); }
    finally { setBusy(false); }
  };

  const runInsights = async () => {
    setInsightsBusy(true); setInsights(null);
    try { const { data } = await api.post(`/documents/${active.id}/insights`); setInsights(data); }
    finally { setInsightsBusy(false); }
  };

  const ask = async (e) => {
    e.preventDefault();
    if (!q.trim() || !active) return;
    const userMsg = { role: "user", content: q }; setChat((c) => [...c, userMsg]); setQ("");
    const { data } = await api.post(`/documents/${active.id}/ask`, { question: userMsg.content });
    setChat((c) => [...c, { role: "assistant", content: data.answer }]);
  };

  return (
    <div className="space-y-5" data-testid="documents-page">
      <div>
        <SectionLabel>Document Intelligence</SectionLabel>
        <h1 className="mt-1 text-3xl font-bold tracking-tight">Upload, summarize & <span className="gradient-text">chat with documents</span></h1>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files?.[0]; if (f) onUpload(f); }}
        className={`relative rounded-[24px] border-2 border-dashed p-10 text-center transition ${drag ? "border-cyan-400/60 bg-cyan-500/5" : "border-white/10 bg-white/[0.02]"}`}
        data-testid="doc-dropzone"
      >
        <input ref={fileRef} type="file" hidden onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0])} data-testid="doc-file-input" />
        <Upload className="h-7 w-7 mx-auto text-cyan-300" />
        <div className="mt-2 text-sm">Drop files here or <button onClick={() => fileRef.current?.click()} className="text-cyan-300 underline" data-testid="doc-browse">browse</button></div>
        <div className="text-[11px] text-slate-500 mt-1">PDF · DOCX · XLSX · PPTX · TXT — up to 25MB</div>
      </div>

      <div className="grid lg:grid-cols-[320px_1fr] gap-5">
        <GlassCard className="p-3 max-h-[70vh] overflow-auto">
          <SectionLabel>Library</SectionLabel>
          <div className="mt-2 space-y-1">
            {docs.length === 0 && <div className="text-xs text-slate-500 p-3">No documents uploaded yet.</div>}
            {docs.map((d) => (
              <button key={d.id} onClick={() => { setActive(d); setSummary(d.summary || ""); setChat([]); }} className={`w-full text-left p-3 rounded-xl flex items-center gap-2 ${active?.id === d.id ? "bg-white/[0.06] border border-white/10" : "hover:bg-white/[0.04]"}`}>
                <FileText className="h-4 w-4 text-cyan-300" />
                <div className="min-w-0">
                  <div className="text-sm truncate">{d.name}</div>
                  <div className="text-[10px] font-mono text-slate-500 uppercase tracking-wider">{(d.size / 1024).toFixed(1)} KB · {d.owner}</div>
                </div>
              </button>
            ))}
          </div>
        </GlassCard>

        <GlassCard className="p-6">
          {!active && <div className="text-slate-400">Upload or select a document to start.</div>}
          {active && (
            <motion.div key={active.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-300">{active.mime || "document"}</div>
                  <h2 className="text-xl font-bold">{active.name}</h2>
                </div>
                <div className="flex gap-2">
                  <GhostButton onClick={runInsights} disabled={insightsBusy} data-testid="doc-insights"><Sparkles className="h-4 w-4" /> {insightsBusy ? "Analyzing…" : "AI Insights"}</GhostButton>
                  <GradientButton onClick={summarize} disabled={busy} data-testid="doc-summarize"><Sparkles className="h-4 w-4" /> {busy ? "Summarizing…" : "AI Summary"}</GradientButton>
                </div>
              </div>

              {summary && (
                <div className="mt-5 p-4 rounded-xl bg-white/[0.03] border border-white/10" data-testid="doc-summary-block">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">AI Summary</div>
                  <Markdown source={summary} />
                </div>
              )}

              {insights && (
                <div className="mt-5 grid md:grid-cols-2 gap-4" data-testid="doc-insights-block">
                  <div className="p-4 rounded-xl bg-white/[0.03] border border-white/10">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 flex items-center gap-1"><Tag className="h-3 w-3" /> Keywords & auto-tags</div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {(insights.keywords || []).map((k, i) => (
                        <span key={i} className="text-[10px] font-mono px-2 py-0.5 rounded-md bg-cyan-500/10 text-cyan-300 border border-cyan-500/30">{k}</span>
                      ))}
                      {(insights.auto_tags || []).map((k, i) => (
                        <span key={`t${i}`} className="text-[10px] font-mono px-2 py-0.5 rounded-md bg-violet-500/10 text-violet-300 border border-violet-500/30">#{k}</span>
                      ))}
                    </div>
                    <div className="mt-3 flex items-center gap-3 text-[10px] font-mono text-slate-400">
                      <span>Sentiment: <span className="text-cyan-300 uppercase">{insights.sentiment || "neutral"}</span></span>
                      <span>· Confidence: <span className="text-cyan-300">{Math.round((insights.confidence || 0.7) * 100)}%</span></span>
                    </div>
                  </div>
                  <div className="p-4 rounded-xl bg-white/[0.03] border border-white/10">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 flex items-center gap-1"><AtSign className="h-3 w-3" /> Named entities</div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {(insights.entities || []).length === 0 && <span className="text-[11px] text-slate-500">No entities detected.</span>}
                      {(insights.entities || []).slice(0, 12).map((e, i) => (
                        <span key={i} className="text-[10px] font-mono px-2 py-0.5 rounded-md bg-white/[0.04] text-slate-300 border border-white/10">
                          <span className="text-slate-200">{e.text}</span> <span className="text-slate-500">· {e.type}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                  {(insights.related_incidents || []).length > 0 && (
                    <div className="p-4 rounded-xl bg-white/[0.03] border border-white/10 md:col-span-2">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 flex items-center gap-1"><ShieldAlert className="h-3 w-3" /> Related incidents</div>
                      <div className="mt-2 space-y-1.5">
                        {insights.related_incidents.map((ri) => (
                          <Link key={ri.id} to={`/app/incidents/${ri.id}`} className="block text-sm hover:bg-white/[0.04] rounded-lg px-2 py-1">
                            <span className="text-slate-200">{ri.title}</span>
                            <span className="ml-2 text-[10px] font-mono text-slate-500 uppercase tracking-wider">{ri.severity} · {ri.status}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-6">
                <SectionLabel>Chat with this document</SectionLabel>
                <div className="mt-3 space-y-3 max-h-72 overflow-auto pr-1">
                  {chat.length === 0 && <div className="text-xs text-slate-500">Ask anything about this document.</div>}
                  {chat.map((m, i) => (
                    <div key={i} className={`p-3 rounded-xl border ${m.role === "user" ? "bg-[#1E293B] border-white/5" : "bg-cyan-500/[0.04] border-cyan-500/20"}`}>
                      {m.role === "assistant" ? <Markdown source={m.content} /> : <div className="text-sm">{m.content}</div>}
                    </div>
                  ))}
                </div>
                <form onSubmit={ask} className="mt-3 flex gap-2">
                  <input data-testid="doc-chat-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ask about this document…" className="flex-1 bg-white/[0.04] border border-white/10 rounded-full px-4 py-2 text-sm" />
                  <GradientButton type="submit" data-testid="doc-chat-send"><Send className="h-4 w-4" /></GradientButton>
                </form>
              </div>
            </motion.div>
          )}
        </GlassCard>
      </div>
    </div>
  );
}
